import { useState, useEffect } from 'react'
import {
  getMetaConnections,
  getMetaAccountCampaigns,
  getMetaDailyInsights,
  type MetaConnection,
  type DailyInsightRow,
} from '@/lib/api/oauth'
import { reportAttributionFailure } from '@/lib/observability/attribution'

interface UseMetaAdsResult {
  metaConnections: MetaConnection[]
  metaCampaignsData: any[]
  dailyInsights: DailyInsightRow[]
  isLoadingMeta: boolean
  metaError: string | null
}

export function useMetaAds(
  propertyId: string | undefined,
  token: string | undefined,
  dateRange: { since: string; until: string } | null,
): UseMetaAdsResult {
  const [metaConnections, setMetaConnections] = useState<MetaConnection[]>([])
  const [metaCampaignsData, setMetaCampaignsData] = useState<any[]>([])
  const [dailyInsights, setDailyInsights] = useState<DailyInsightRow[]>([])
  const [isLoadingMeta, setIsLoadingMeta] = useState(false)
  const [metaError, setMetaError] = useState<string | null>(null)

  useEffect(() => {
    if (!propertyId || !token) return
    let stale = false

    const fetchMeta = async () => {
      setIsLoadingMeta(true)
      setMetaError(null)
      try {
        const connections = await getMetaConnections(propertyId, token)
        if (stale) return
        setMetaConnections(Array.isArray(connections) ? connections : [])

        const campaignsPromises = (Array.isArray(connections) ? connections : []).map(async (conn) => {
          if (!conn.adAccounts?.length) return null
          const perAccount = conn.adAccounts.map(async (acct) => {
            try {
              const data = await getMetaAccountCampaigns(conn.id, acct.id, token, dateRange ?? undefined)
              let daily: DailyInsightRow[] = []
              if (dateRange) {
                try {
                  const res = await getMetaDailyInsights(conn.id, acct.id, token, dateRange.since, dateRange.until)
                  daily = Array.isArray(res?.insights) ? res.insights : []
                } catch { /* daily insights are optional */ }
              }
              return { connectionId: conn.id, accountId: acct.id, accountName: acct.name, ...data, daily }
            } catch { return null }
          })
          return Promise.all(perAccount)
        })

        const all = await Promise.all(campaignsPromises)
        if (stale) return
        const flat = all.flat().filter((x): x is NonNullable<typeof x> => x !== null)
        setMetaCampaignsData(flat)

        const allDaily = flat.flatMap((d: any) => d.daily || [])
        setDailyInsights(allDaily)
      } catch (err) {
        reportAttributionFailure('meta_ads', err, { propertyId })
        if (stale) return
        setMetaError(err instanceof Error ? err.message : 'Failed to load Meta ads data')
        setMetaCampaignsData([])
        setDailyInsights([])
      } finally {
        if (!stale) setIsLoadingMeta(false)
      }
    }
    fetchMeta()

    return () => { stale = true }
  }, [propertyId, token, dateRange?.since, dateRange?.until])

  return { metaConnections, metaCampaignsData, dailyInsights, isLoadingMeta, metaError }
}
